import { useState, useCallback, Dispatch, SetStateAction } from "react";
import { useDispatch } from "react-redux";
import { FetchedOrderUploadProduct } from "../types";
import { AlertContent } from "../../../interfaces/interfaces";
import { AlertType } from "../../../interfaces/enums";
import { OrderUploadResult } from "../ModalSwitcher";
import { updateCurrentOrder } from "../../../redux/actions/orders";
import uploadFile from "../actions/uploadFile";
import removeItem from "../actions/removeItem";
import reloadItem from "../actions/reloadItem";
import clearCartFetch from "../fetchs/clearCart";
import { IProgressBarLevel } from "../types";

interface UseOrderUploaderReturn {
  // State
  fetchedProducts: FetchedOrderUploadProduct[];
  toast: AlertContent | null;
  isLoading: boolean;
  modalToShow: OrderUploadResult | null;
  progressBarLevel: IProgressBarLevel;

  // Actions
  handleFileUpload: (csvEachLineArray: string[][]) => Promise<void>;
  handleRemoveItem: (sku: string) => void;
  handleReloadItem: (sku: string) => void;
  handleClearCart: () => Promise<void>;
  setToast: Dispatch<SetStateAction<AlertContent | null>>;
  setModalToShow: Dispatch<SetStateAction<OrderUploadResult | null>>;
  setFetchedProducts: Dispatch<SetStateAction<FetchedOrderUploadProduct[]>>;
}

export const useOrderUploader = (): UseOrderUploaderReturn => {
  const dispatch = useDispatch();
  const [fetchedProducts, setFetchedProducts] = useState<FetchedOrderUploadProduct[]>([]);
  const [toast, setToast] = useState<AlertContent | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [modalToShow, setModalToShow] = useState<OrderUploadResult | null>(null);
  const [progressBarLevel, setProgressBarLevel] = useState<IProgressBarLevel>({
    level: 0,
    totalItems: 0,
  });

  const handleFileUpload = useCallback(async (csvEachLineArray: string[][]) => {
    setModalToShow("progressBarLevel");
    const { newFetchedProducts } = await uploadFile({
      csvEachLineArray,
      setIsLoading,
      setToast,
      setProgressBarLevel,
    });
    setModalToShow(null);

    setFetchedProducts([...newFetchedProducts]);
  }, []);

  const handleRemoveItem = useCallback(
    (sku: string) => {
      removeItem({ sku, fetchedProducts, setFetchedProducts });
    },
    [fetchedProducts],
  );

  const handleReloadItem = useCallback(
    (sku: string) => {
      reloadItem({
        sku,
        fetchedProducts,
        setFetchedProducts,
        setModalToShow,
        setToast,
        setIsLoading,
      });
    },
    [fetchedProducts],
  );

  const handleClearCart = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await clearCartFetch();
      const isSuccess = result.code === "success";

      if (isSuccess && result.data) {
        dispatch(updateCurrentOrder(result.data));
      }

      setModalToShow(isSuccess ? null : modalToShow);
      setToast({
        message: isSuccess ? "Cart cleared successfully" : result.message || "Failed to clear cart",
        type: isSuccess ? AlertType.Success : AlertType.Error,
      });
    } catch (error) {
      setToast({
        message: "Failed to clear cart",
        type: AlertType.Error,
      });
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, modalToShow]);

  return {
    // State
    fetchedProducts,
    toast,
    isLoading,
    modalToShow,
    progressBarLevel,

    // Actions
    handleFileUpload,
    handleRemoveItem,
    handleReloadItem,
    handleClearCart,
    setToast,
    setModalToShow,
    setFetchedProducts,
  };
};
